/*
 * The sheet pane (T169; decision 0611): a frame of its own size that scrolls
 * inside itself, with the paper drawn at the scale that puts a whole page inside
 * it. 🔨 The pane keeps its size and the paper is what moves, which is what stops
 * a page break from moving the viewing window.
 */
/*
 * The zoom row above the pane and the pane beneath it (decision 0659). 🔒 It takes
 * the height its page hands it and passes the rest of that height to the pane
 * (T205; decision 0694), so the pane is the one thing on the PDF view that scrolls.
 */
.frameWrap {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

/*
 * The zoom control and the page count at the row's left and the page's view
 * controls at its right, one row above the pane (decisions 0659, 0626; T335). 🔒 The
 * row wraps rather than overflows: on a phone the view controls drop to a line of
 * their own, still at the right.
 */
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
}

.toolbarStart {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
}

.toolbarEnd {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  min-width: 0;
}

/*
 * 🔒 The quiet row (T2190; decisions 3051, 6703): the switch at its head, the zoom
 * as − and +, Fit Page and the page count, and the download at its right, with the
 * gaps closed to the smallest step.
 *
 * 🔒 Its parts never stand over each other (decision 11097, red landing 41139296157).
 * Where the frame cannot hold the switch, the zoom and the download on one line, the
 * row wraps: the zoom, the page count and the download take the next line, the
 * download still at its right. 💰 Held on one line, the zoom kept its own width in a
 * 260-pixel sheet column (1280 wide, the list and the notes rail beside it), and
 * Fit Page drew over the download, so nobody could press it.
 */
.toolbarLead {
  display: flex;
  flex: none;
  align-items: center;
}

.toolbar[data-document-sheet-toolbar-shape='row'] {
  flex-wrap: wrap;
  gap: var(--space-1);
}

.toolbar[data-document-sheet-toolbar-shape='row'] > .toolbarEnd {
  flex: none;
  flex-wrap: nowrap;
}

/*
 * 🔨 The zoom part grows from nothing and never shrinks below the zoom itself, so the
 * row breaks its line on the zoom's own width and the page count takes what is left.
 * The count is the one part that gives (T2235): it asks for no width of its own and
 * shortens to an ellipsis, so it alone never moves the download to another line.
 */
.toolbar[data-document-sheet-toolbar-shape='row'] > .toolbarStart {
  flex: 1 1 0;
  flex-wrap: nowrap;
  gap: var(--space-1);
  min-width: min-content;
}

.toolbar[data-document-sheet-toolbar-shape='row'] .pageCount {
  flex: 1 1 0;
  width: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The pane and the zoom row's remainder of the height. */
.paneWrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/*
 * 🔨 A height and not a ceiling (decision 0659). Fit page is measured against the
 * pane's own box, and a pane whose height followed its content measured a sheet
 * drawn at half size as a smaller pane, so every step out shrank the fit again.
 *
 * 🔒 The height is the room the page leaves beneath the zoom row, never a guess at
 * the chrome around it (T205; decision 0694). The owner, 2026-09-24: *"get rid of
 * double scroll: the sheet view has a scroll and so does the document details, no
 * need for two."* 💰 It was `min(720px, 100vh - 20rem)`, an allowance measured on
 * one window, and wherever the page's own rows took more than that allowance the
 * content region scrolled beside the pane. The floor is what a window too short
 * for any sheet still gets, and there the content region scrolls instead.
 */
.pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  min-height: var(--document-sheet-pane-min-height);
  overflow: auto;
  padding: var(--space-2);
  border-radius: var(--radius-medium);
  background: var(--surface-2);
  /* 🔨 The scrollbar's room is kept whether or not it shows, so a sheet fitted to the width never re-measures itself narrower as its own scrollbar appears (decision 1452). */
  scrollbar-gutter: stable;
}

/*
 * 🔒 In the page's flow on a phone (T2189; decision 3050): the pane draws the
 * sheet at its own height and scrolls only sideways, so the page is the one
 * scroller and what follows the sheet stands after it. 🔨 No floor, because the
 * pane's height is the sheet's own and the page around it scrolls.
 */
.frameWrap[data-document-sheet-flow='true'],
.frameWrap[data-document-sheet-flow='true'] > .paneWrap {
  flex: none;
}

.frameWrap[data-document-sheet-flow='true'] .pane {
  flex: none;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-gutter: auto;
}

/*
 * The stage is the sheet as a reader sees it, at the scale in force: it is what
 * the pane scrolls and what the paper is centred as. 💰 The frame inside it stays
 * the page's own pixels, because the HTML it loads draws its page at fixed
 * physical inches and a frame given another box crops it rather than scaling it.
 */
.stage {
  position: relative;
  flex: none;
  /* 🔨 Centred by its own margins, which fall to nothing once the sheet is wider than the pane, so a zoomed sheet scrolls to its left edge rather than being cut there. */
  margin-inline: auto;
}

.frame {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  border: 0;
  background: var(--surface);
  box-shadow: var(--tile-shadow);
}

/* The rule where the printer would break, drawn on the screen's side of the frame and never on the paper. */
.break {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1px dashed var(--border);
  pointer-events: none;
}

.pageCount {
  flex: none;
  margin: 0;
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

/*
 * 🔒 The status band's layer over the first page (decision 1457): the paper's own
 * box, scaled as the paper is, clipping whatever it holds to the paper's edge. It
 * takes no press, so a click on the corner still reaches nothing it did not before.
 */
.cornerLayer {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  overflow: hidden;
  transform-origin: top left;
  pointer-events: none;
}
